import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

const PUBKEY = '6f7c7936efdaec8e51dc3dec3e1b97990151c5a53a077569f4451ad5b48a90b1';

/** Returns true if the event has a content-warning tag (NIP-36). */
function hasContentWarning(event: { tags: string[][] }): boolean {
  return event.tags.some(t => t[0] === 'content-warning');
}

export function useAuthorNotes() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-notes', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query([{ kinds: [1], authors: [PUBKEY], limit: 20 }], { signal: c.signal });
      return events.filter(e => !hasContentWarning(e));
    },
  });
}

export function useAuthorImages() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-images', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query([{ kinds: [20, 1063], authors: [PUBKEY], limit: 20 }], { signal: c.signal });
      return events.filter(e => !hasContentWarning(e));
    },
  });
}

export function useAuthorVideos() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-videos', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [21, 22, 34235, 34236], authors: [PUBKEY], limit: 20 }],
        { signal: c.signal },
      );
      return events.filter(e => !hasContentWarning(e));
    },
  });
}

// Music: kind 36787 (Music Track — addressable, per NIP-PS Music Tracks spec)
export function useAuthorMusic() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-music', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [36787], authors: [PUBKEY], limit: 20 }],
        { signal: c.signal },
      );
      return events.filter(e => !hasContentWarning(e));
    },
  });
}

// PressStr: kind 34609 (Magazine), 39731 (Magazine Issue), 33953 (Ebook)
export function useAuthorMagazines() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-magazines', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [34609, 39731, 33953], authors: [PUBKEY], limit: 20 }],
        { signal: c.signal },
      );
      return events.filter(e => !hasContentWarning(e));
    },
  });
}

// ── CW-only variants (for the bound-hands subpage) ────────────────────────────

export function useAuthorNotesCW() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-notes-cw', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query([{ kinds: [1], authors: [PUBKEY], limit: 20 }], { signal: c.signal });
      return events.filter(e => hasContentWarning(e));
    },
  });
}

export function useAuthorImagesCW() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-images-cw', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query([{ kinds: [20, 1063], authors: [PUBKEY], limit: 20 }], { signal: c.signal });
      return events.filter(e => hasContentWarning(e));
    },
  });
}

export function useAuthorVideosCW() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-videos-cw', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [21, 22, 34235, 34236], authors: [PUBKEY], limit: 20 }],
        { signal: c.signal },
      );
      return events.filter(e => hasContentWarning(e));
    },
  });
}

export function useAuthorMusicCW() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-music-cw', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [36787], authors: [PUBKEY], limit: 20 }],
        { signal: c.signal },
      );
      return events.filter(e => hasContentWarning(e));
    },
  });
}

export function useAuthorMagazinesCW() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['author-magazines-cw', PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [34609, 39731, 33953], authors: [PUBKEY], limit: 20 }],
        { signal: c.signal },
      );
      return events.filter(e => hasContentWarning(e));
    },
  });
}
